import { useMutation } from "@apollo/client/react";
import { ArrowRight, CalendarDays, Check, Gift, Mail, Send } from "lucide-react";
import type { InferGetServerSidePropsType, NextPage } from "next";
import Head from "next/head";
import NextLink from "next/link";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import { useMemo, useState } from "react";
import { b } from "@/b";
import { HEADER_FRAGMENT } from "@/components/Header";
import { PageLayout } from "@/components/PageLayout";
import { TitaniumGiftCard } from "@/components/pages/gift-cards/TitaniumGiftCard";
import { CartItemGiftCard_Fragment } from "@/graphql/fragments";
import { popupMiddleware } from "@/sanity/popup-middleware";
import { getApolloSSRProps, useHydratedQuery } from "@/util/apollo-client";
import {
  formatGiftCardSendOn,
  GIFT_CARD_MAX_DOLLARS,
  GIFT_CARD_MESSAGE_MAX_LENGTH,
  GIFT_CARD_MIN_DOLLARS,
  GIFT_CARD_PRESET_AMOUNTS,
} from "@/util/giftCards";

const GIFT_CARDS_QUERY = b.query("GiftCardsPage", (b) => [
  b.viewer((b) => [
    b.id(),
    b.account((b) => [b.id(), b.firstName(), b.lastName()]),
    b.cart((b) => [
      b.items((b) => [
        b.id(),
        b.__on("CartItemGiftCard", (b) => [b.__fragment(CartItemGiftCard_Fragment)]),
      ]),
    ]),
  ]),
  b.__fragment(HEADER_FRAGMENT),
]);

const AddGiftCard_Mutation = b.mutation(
  "GiftCardsPage_Add",
  { input: "GiftCardItemInput!" },
  (b, v) => [
    b.viewer((b) => [b.cart((b) => [b.addGiftCardItem({ input: v.input }, (b) => [b.id()])])]),
  ],
);

const EditGiftCard_Mutation = b.mutation(
  "GiftCardsPage_Edit",
  { id: "String!", input: "GiftCardItemInput!" },
  (b, v) => [
    b.viewer((b) => [
      b.cart((b) => [
        b.item({ id: v.id }, (b) => [
          b.__on("CartItemGiftCardMutation", (b) => [b.edit({ input: v.input }, (b) => [b.id()])]),
        ]),
      ]),
    ]),
  ],
);

const inputClass =
  "w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-brand-black placeholder:text-gray-400 outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint/30 transition-all";

function toDateInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const GiftCardsPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const { data } = useHydratedQuery(props);
  const router = useRouter();
  const editId = typeof router.query.edit === "string" ? router.query.edit : null;

  const editing = useMemo(() => {
    const item = data.viewer.cart.items.find((i) => i.id === editId);
    return item?.__typename === "CartItemGiftCard" ? item : null;
  }, [data.viewer.cart.items, editId]);

  const account = data.viewer.account;
  const defaultFrom = [account?.firstName, account?.lastName].filter(Boolean).join(" ");

  const initialAmount = editing?.amount.value ?? 5000;
  const [amount, setAmount] = useState<number>(initialAmount);
  const [customDollars, setCustomDollars] = useState(
    GIFT_CARD_PRESET_AMOUNTS.includes(initialAmount) ? "" : String(initialAmount / 100),
  );
  const [recipientName, setRecipientName] = useState(editing?.recipientName ?? "");
  const [recipientEmail, setRecipientEmail] = useState(editing?.recipientEmail ?? "");
  const [senderName, setSenderName] = useState(editing?.senderName ?? defaultFrom);
  const [message, setMessage] = useState(editing?.message ?? "");
  const [schedule, setSchedule] = useState(Boolean(editing?.sendOn));
  const [sendOn, setSendOn] = useState(editing?.sendOn ?? "");
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);

  const [addGiftCard, { loading: adding }] = useMutation(AddGiftCard_Mutation.document(), {
    refetchQueries: ["GiftCardsPage"],
  });
  const [editGiftCard, { loading: savingEdit }] = useMutation(EditGiftCard_Mutation.document());

  const today = useMemo(() => new Date(), []);
  const minDate = toDateInput(new Date(today.getTime() + 24 * 60 * 60 * 1000));
  const maxDate = toDateInput(new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000));

  const amountValid =
    Number.isInteger(amount / 100) &&
    amount >= GIFT_CARD_MIN_DOLLARS * 100 &&
    amount <= GIFT_CARD_MAX_DOLLARS * 100;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim());
  const canSubmit =
    amountValid &&
    recipientName.trim() !== "" &&
    emailValid &&
    senderName.trim() !== "" &&
    (!schedule || sendOn !== "") &&
    !adding &&
    !savingEdit;

  const resetForm = () => {
    setRecipientName("");
    setRecipientEmail("");
    setMessage("");
    setSchedule(false);
    setSendOn("");
    setAdded(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    const input = {
      amount,
      recipientName: recipientName.trim(),
      recipientEmail: recipientEmail.trim(),
      senderName: senderName.trim(),
      message: message.trim() || null,
      sendOn: schedule ? sendOn : null,
    };
    try {
      if (editing) {
        await editGiftCard({ variables: { id: editing.id, input } });
        await router.push("/cart");
        return;
      }
      await addGiftCard({ variables: { input } });
      posthog.capture("gift_card_added_to_cart", { amount, scheduled: schedule });
      setAdded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    }
  };

  return (
    <>
      <Head>
        <title>{`Gift Cards | Visionary Meals`}</title>
        <meta
          name="description"
          content="Send a Visionary Meals eGift card — delivered by email, instantly or on the date you choose."
        />
      </Head>
      <PageLayout data={data} noPadding>
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-0 -z-10"
            style={{
              background: "linear-gradient(135deg, #8debcf15 0%, #ffffff 50%, #efeff108 100%)",
            }}
          />
          <div className="max-w-4xl mx-auto px-6 pt-14 pb-8 lg:pt-20 text-center">
            <h1 className="inline-flex items-center justify-center gap-3 text-3xl lg:text-4xl font-bold text-brand-black leading-tight">
              <Gift className="size-8 lg:size-10 text-brand-mint" />
              Give the Gift of Great Meals
            </h1>
            <p className="text-base text-gray-500 mt-3 max-w-lg mx-auto">
              eGift cards are emailed with a personal note — right away, or on the day you pick. No
              delivery needed.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 lg:px-8 pb-16 lg:pb-24">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Preview */}
            <div className="lg:w-[380px] shrink-0">
              <div className="lg:sticky lg:top-24">
                <TitaniumGiftCard
                  amount={amountValid ? `$${amount / 100}` : "$—"}
                  caption={`${recipientName.trim() ? `For ${recipientName.trim()}` : "eGift Card"}${
                    senderName.trim() ? ` · From ${senderName.trim()}` : ""
                  }`}
                />
                {message.trim() && (
                  <p className="mt-4 text-sm text-gray-600 italic text-center break-words">
                    “{message.trim()}”
                  </p>
                )}
                <ul className="mt-6 space-y-2 text-xs text-gray-500">
                  <li className="flex items-center gap-2">
                    <Mail className="size-3.5 text-brand-mint" /> Delivered by email with a redeem
                    link
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="size-3.5 text-brand-mint" /> Balance never expires
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="size-3.5 text-brand-mint" /> Check out on its own or with your
                    meals
                  </li>
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="flex-1 min-w-0">
              {added ? (
                <div className="rounded-2xl border border-brand-mint/40 bg-brand-mint/10 p-6 text-center space-y-4">
                  <div className="mx-auto flex items-center justify-center size-12 rounded-full bg-brand-mint">
                    <Check className="size-6 text-brand-black" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-brand-black">Added to your cart</p>
                    <p className="text-sm text-gray-600 mt-1">
                      ${amount / 100} for {recipientName.trim()} ·{" "}
                      {formatGiftCardSendOn(schedule ? sendOn : null)}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <NextLink
                      href="/cart"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-brand-mint text-brand-black text-sm font-semibold hover:bg-brand-mint/90 transition-colors no-underline active:scale-[0.98]"
                    >
                      Go to cart
                      <ArrowRight className="size-4" />
                    </NextLink>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      Send another
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Amount */}
                  <fieldset className="space-y-3">
                    <legend className="text-sm font-semibold text-brand-black mb-3">Amount</legend>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {GIFT_CARD_PRESET_AMOUNTS.map((preset) => {
                        const selected = customDollars === "" && amount === preset;
                        return (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => {
                              setAmount(preset);
                              setCustomDollars("");
                            }}
                            className={`py-3 rounded-xl border text-sm font-semibold transition-all cursor-pointer active:scale-[0.98] ${
                              selected
                                ? "border-brand-mint bg-brand-mint/15 text-brand-black ring-1 ring-brand-mint"
                                : "border-gray-200 text-gray-700 hover:border-gray-300"
                            }`}
                          >
                            ${preset / 100}
                          </button>
                        );
                      })}
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                        $
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={GIFT_CARD_MIN_DOLLARS}
                        max={GIFT_CARD_MAX_DOLLARS}
                        step={1}
                        placeholder={`Custom amount (${GIFT_CARD_MIN_DOLLARS}–${GIFT_CARD_MAX_DOLLARS})`}
                        value={customDollars}
                        onChange={(e) => {
                          setCustomDollars(e.target.value);
                          const dollars = Number(e.target.value);
                          setAmount(Number.isFinite(dollars) ? Math.round(dollars * 100) : 0);
                        }}
                        className={`${inputClass} pl-7`}
                      />
                    </div>
                    {customDollars !== "" && !amountValid && (
                      <p className="text-xs text-red-600">
                        Enter a whole-dollar amount from ${GIFT_CARD_MIN_DOLLARS} to $
                        {GIFT_CARD_MAX_DOLLARS}.
                      </p>
                    )}
                  </fieldset>

                  {/* Recipient */}
                  <fieldset className="space-y-3">
                    <legend className="text-sm font-semibold text-brand-black mb-3">
                      Who's it for?
                    </legend>
                    <input
                      type="text"
                      placeholder="Recipient's name"
                      value={recipientName}
                      maxLength={80}
                      onChange={(e) => setRecipientName(e.target.value)}
                      className={inputClass}
                    />
                    <input
                      type="email"
                      placeholder="Recipient's email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      className={inputClass}
                    />
                    <input
                      type="text"
                      placeholder="From (your name)"
                      value={senderName}
                      maxLength={80}
                      onChange={(e) => setSenderName(e.target.value)}
                      className={inputClass}
                    />
                    <div>
                      <textarea
                        placeholder="Add a personal message (optional)"
                        value={message}
                        maxLength={GIFT_CARD_MESSAGE_MAX_LENGTH}
                        rows={3}
                        onChange={(e) => setMessage(e.target.value)}
                        className={`${inputClass} resize-none`}
                      />
                      <p className="text-[11px] text-gray-400 text-right">
                        {message.length}/{GIFT_CARD_MESSAGE_MAX_LENGTH}
                      </p>
                    </div>
                  </fieldset>

                  {/* Delivery */}
                  <fieldset className="space-y-3">
                    <legend className="text-sm font-semibold text-brand-black mb-3">
                      When should we send it?
                    </legend>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: false, label: "Send now", icon: Send },
                        { value: true, label: "Schedule", icon: CalendarDays },
                      ].map(({ value, label, icon: Icon }) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => setSchedule(value)}
                          className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-semibold transition-all cursor-pointer active:scale-[0.98] ${
                            schedule === value
                              ? "border-brand-mint bg-brand-mint/15 text-brand-black ring-1 ring-brand-mint"
                              : "border-gray-200 text-gray-700 hover:border-gray-300"
                          }`}
                        >
                          <Icon className="size-4" />
                          {label}
                        </button>
                      ))}
                    </div>
                    {schedule ? (
                      <div>
                        <input
                          type="date"
                          min={minDate}
                          max={maxDate}
                          value={sendOn}
                          onChange={(e) => setSendOn(e.target.value)}
                          className={inputClass}
                        />
                        <p className="text-xs text-gray-500 mt-1.5">
                          Emailed at 9am Eastern on the chosen date.
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">
                        Emailed as soon as your payment goes through.
                      </p>
                    )}
                  </fieldset>

                  {error && (
                    <p className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.98] disabled:cursor-not-allowed ${
                      canSubmit
                        ? "bg-brand-mint text-brand-black hover:bg-brand-mint/90 cursor-pointer"
                        : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    {editing
                      ? savingEdit
                        ? "Saving…"
                        : "Save changes"
                      : adding
                        ? "Adding…"
                        : `Add ${amountValid ? `$${amount / 100} ` : ""}gift card to cart`}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
};

export default GiftCardsPage;

export const getServerSideProps = popupMiddleware(async (ctx) => {
  const props = await getApolloSSRProps(GIFT_CARDS_QUERY.document(), {}, ctx);
  return { props };
});
